/**
 * @file appearance-numbers.ts
 * @description Числа «Оформления» сайта (phase19.16, партия SB120, этап 2
 * «оформление сайта числами»; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-stage2.md §2, §5): ступени-
 * подсказки ползунков (прежние ступени v1 — только подсказки, совпавшая
 * подсвечена), предупреждения вне рекомендуемого (не запрет), подписи «ПК
 * 34 → 38 px» и сводки свёрнутых групп числами.
 *
 * Пределы — `SITE_THEME_LIMITS` (shared, theme-limits.ts), значения ступеней —
 * таблицы перевода v1 (legacy-v1.ts): одна правда для перевода и подсказок.
 * Новая подсказка — запись здесь, а не ветка в компоненте.
 */

import {
  BUTTON_RADIUS_V1,
  DENSITY_V1,
  EYEBROW_TRACKING_V1,
  FRAME_SHADOW_V1,
  HAZE_STRENGTH_V1,
  HEADING_SCALE_V1,
  SITE_THEME_LIMITS,
  type SiteButtonRadius,
  type SiteTheme,
} from "@chaiberi/shared";

import type { NumberPreset } from "../../components/site-fields/NumberPresets";
import { formatNumber } from "../site-fields/number-input";

const TL = SITE_THEME_LIMITS;

/** Число по-русски: 0,875; без хвостов. */
const n = (value: number, decimals = 3): string => formatNumber(value, decimals);

/** Рекомендация поля — предупреждение вне диапазона (`NumberSliderField.recommended`). */
export interface Recommended {
  min: number;
  max: number;
  text: string;
}

/** Значение вне рекомендуемого — «!» у группы (SB122) из того же правила, что предупреждение поля. */
export function outOfRecommended(value: number, rec: Recommended): boolean {
  return value < rec.min || value > rec.max;
}

// ── «Шрифты» ──

export const HEADING_SCALE_PRESETS: readonly NumberPreset[] = [
  { label: `Мельче ${n(HEADING_SCALE_V1.s)}`, value: HEADING_SCALE_V1.s },
  { label: `Обычный ${n(HEADING_SCALE_V1.m)}`, value: HEADING_SCALE_V1.m },
  { label: `Крупнее ${n(HEADING_SCALE_V1.l)}`, value: HEADING_SCALE_V1.l },
];

/** Заголовок раздела при ×1 (apps/web globals.css `.text-h2`: 24 px телефон, 34 px с 768). */
export const SECTION_TITLE_PX = { desktop: 34, phone: 24 } as const;

/** «Заголовок раздела: ПК 34 → 38 px, телефон 24 → 27 px». */
export function headingScaleHint(scale: number): string {
  const px = (base: number) => Math.round(base * scale);
  return `Заголовок раздела: ПК ${SECTION_TITLE_PX.desktop} → ${px(SECTION_TITLE_PX.desktop)} px, телефон ${SECTION_TITLE_PX.phone} → ${px(SECTION_TITLE_PX.phone)} px`;
}

export const HEADING_SCALE_RECOMMENDED: Recommended = {
  min: 0.85,
  max: TL.headingScale.max,
  text: "Заголовок раздела на телефоне станет мельче 21 px — заголовок теряется среди текста.",
};

export const TRACKING_PRESETS: readonly NumberPreset[] = [
  { label: "Обычная 0", value: EYEBROW_TRACKING_V1.normal },
  { label: `Широкая ${n(EYEBROW_TRACKING_V1.wide)}`, value: EYEBROW_TRACKING_V1.wide },
  { label: `Очень широкая ${n(EYEBROW_TRACKING_V1.ultra)}`, value: EYEBROW_TRACKING_V1.ultra },
];

export const TRACKING_RECOMMENDED: Recommended = {
  min: TL.eyebrowTracking.min,
  max: 0.25,
  text: "Длинная надпись капсом не влезет в строку на телефоне.",
};

// ── «Кнопки и карточки» ──

const ROUNDED = BUTTON_RADIUS_V1.rounded === "pill" ? 10 : BUTTON_RADIUS_V1.rounded;
export const BUTTON_RADIUS_PRESETS: readonly NumberPreset[] = [
  { label: "Прямые 0", value: 0 },
  { label: `Скруглённые ${ROUNDED}`, value: ROUNDED },
  { label: "Мягкие 20", value: 20 },
];

/** Скругление кнопок словом для сводки: «пилюля» / «10 px». */
export function buttonRadiusText(radius: SiteButtonRadius): string {
  return radius === "pill" ? "пилюля" : `${n(radius, 2)} px`;
}

/** Сводка группы «Кнопки»: «пилюля · со стрелкой · прописные». */
export function buttonsSummary(shapes: SiteTheme["shapes"]): string {
  return [buttonRadiusText(shapes.buttonRadius), shapes.buttonArrow ? "со стрелкой" : null, shapes.buttonCase === "upper" ? "прописные" : null]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

export const CARD_SHADOW_PRESETS: readonly NumberPreset[] = [
  { label: "Нет 0", value: 0 },
  { label: `Как было ${TL.cardShadow.legacy}`, value: TL.cardShadow.legacy },
  { label: "Плотная 100", value: 100 },
];

export const BORDER_WIDTH_PRESETS: readonly NumberPreset[] = [
  { label: "Нет 0", value: 0 },
  { label: "1", value: 1 },
  { label: "2", value: 2 },
];

export const DENSITY_RECOMMENDED: Recommended = {
  min: 0.7,
  max: TL.density.max,
  text: "Кнопки и поля не сжимаются (48 px) — сжимаются только расстояния.",
};

export const DENSITY_PRESETS: readonly NumberPreset[] = [
  { label: `Компактно ${n(DENSITY_V1.compact)}`, value: DENSITY_V1.compact },
  { label: `Обычно ${n(DENSITY_V1.normal)}`, value: DENSITY_V1.normal },
  { label: `Просторно ${n(DENSITY_V1.spacious)}`, value: DENSITY_V1.spacious },
];

/** Сводка «Отступов сайта»: «×1 · телефон 60 %». */
export function spacingSummary(spacing: SiteTheme["spacing"]): string {
  return `×${n(spacing.density, 2)} · телефон ${Math.round(spacing.phoneFactor * 100)} %`;
}

// ── «Фон и рамка» ──

export const HAZE_PRESETS: readonly NumberPreset[] = [
  { label: "Нет 0", value: HAZE_STRENGTH_V1.off },
  { label: `Мягкое ${HAZE_STRENGTH_V1.on}`, value: HAZE_STRENGTH_V1.on },
  { label: "Сильное 35", value: 35 },
];

export const HAZE_RECOMMENDED: Recommended = {
  min: TL.hazeStrength.min,
  max: 35,
  text: "Свечение спорит с текстом — проверьте на канве.",
};

export const FRAME_WIDTH_PRESETS: readonly NumberPreset[] = [1280, 1440, 1536, 1600].map((value) => ({ label: String(value), value }));

export const FRAME_WIDTH_RECOMMENDED: Recommended = {
  min: 1280,
  max: TL.frameMaxWidth.max,
  text: "Блоки «по сетке» станут уже 1280.",
};

export const FRAME_SHADOW_PRESETS: readonly NumberPreset[] = [
  { label: "Нет 0", value: FRAME_SHADOW_V1.off },
  { label: `Как было ${FRAME_SHADOW_V1.on}`, value: FRAME_SHADOW_V1.on },
  { label: "100", value: 100 },
];


/** Размеры рисунков фона: подсказки ПК (прежние S / M / L). */
export const BACKDROP_SIZE_PRESETS: readonly NumberPreset[] = [
  { label: "S 220", value: 220 },
  { label: "M 320", value: 320 },
  { label: "L 380", value: 380 },
];

